import { MenuItem, Select } from '@src/common/components/Select';
import { css } from '@src/common/styles/Styles';
import type { DriverListing } from '@src/driver/domain/DriverList';
import { useDrivers } from '@src/driver/hooks/useDrivers';
import type { Ref } from 'react';
import { forwardRef } from 'react';
import type { ControllerRenderProps, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props extends Partial<ControllerRenderProps> {
  clientId: string;
  error?: FieldError;
  defaultValue?: { label: string; value: string };
  onDriverSelect: (driver: DriverListing | undefined) => void;
  readOnly?: boolean;
}

export const DriverSelector = forwardRef(function CustomInput(
  { clientId, onChange, onDriverSelect, defaultValue, error, ...props }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const { drivers, isLoading } = useDrivers(clientId);
  const { t } = useTranslation();
  const selectOptions = [
    { label: t('newDriver'), value: '' },
    ...(isLoading
      ? defaultValue
        ? [defaultValue]
        : []
      : drivers.drivers.map((driver) => ({
          value: driver.id,
          label: driver.name,
        }))),
  ];

  return (
    <Select
      ref={ref}
      {...props}
      onChange={(e) => {
        onChange?.(e);
        onDriverSelect(
          drivers.drivers.find(
            (driver) => driver.id === (e.target.value as string),
          ),
        );
      }}
      disabled={isLoading || props.readOnly}
      value={props.value || defaultValue?.value || ''}
      error={error && error.message}
      className={fullWidth}
      label={t(`drivers`)}
    >
      {selectOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

const fullWidth = css({
  flex: 1,
});
